<template>
  <v-data-table
    v-if="items != null"
    :headers="headers"
    :items="items"
    :items-per-page="items_per_page"
    :loading="loading"
    :loading-text="loading_text"
    :no-data-text="no_data_text"
    :footer-props="footer_props"
    class="enhanced-data-table enhanced-data-table--imports elevation-1"
  >
    <!-- Import search specific cells with sensible defaults; pages can override via cell-* slots -->
    <template v-slot:[`item.date`]="slotProps">
      <slot v-if="$scopedSlots['cell-date']" name="cell-date" v-bind="slotProps" />
      <span v-else>{{ $rc.pretty(slotProps.item.import_time,'date') }}</span>
    </template>
    <template v-slot:[`item.time`]="slotProps">
      <slot v-if="$scopedSlots['cell-time']" name="cell-time" v-bind="slotProps" />
      <span v-else>{{ $rc.pretty(slotProps.item.import_time,'time') }}</span>
    </template>
    <template v-slot:[`item.imported_by`]="slotProps">
      <slot v-if="$scopedSlots['cell-imported_by']" name="cell-imported_by" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.imported_by_employee_label }}</span>
    </template>
    <template v-slot:[`item.file_name`]="slotProps">
      <slot v-if="$scopedSlots['cell-file_name']" name="cell-file_name" v-bind="slotProps" />
      <a v-else href="#" @click.prevent="on_click_download_file(slotProps.item)" style="text-decoration: underline;">
        {{ slotProps.item.original_file_name }}
      </a>
    </template>
    <template v-slot:[`item.enriched_data`]="slotProps">
      <slot v-if="$scopedSlots['cell-enriched_data']" name="cell-enriched_data" v-bind="slotProps" />
      <a v-else-if="slotProps.item.enriched_data_blob_id" href="#" @click.prevent="on_click_download_enriched_data(slotProps.item)" style="text-decoration: underline;">
        Download Mapped File
      </a>
      <span v-else style="color: #999;">Not Available</span>
    </template>
    <template v-slot:[`item.state`]="slotProps">
      <slot v-if="$scopedSlots['cell-state']" name="cell-state" v-bind="slotProps" />
      <v-chip v-else :color="get_status_color(slotProps.item.state)" small text-color="white">
        {{ format_status(slotProps.item.state) }}
      </v-chip>
    </template>
    <template v-slot:[`item.records_imported`]="slotProps">
      <slot v-if="$scopedSlots['cell-records_imported']" name="cell-records_imported" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.records_imported || 0 }}</span>
    </template>
    <template v-slot:[`item.list_members_added`]="slotProps">
      <slot v-if="$scopedSlots['cell-list_members_added']" name="cell-list_members_added" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.list_members_added || slotProps.item.records_imported || 0 }}</span>
    </template>
    <template v-slot:[`item.recipients_added`]="slotProps">
      <slot v-if="$scopedSlots['cell-recipients_added']" name="cell-recipients_added" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.recipients_added || slotProps.item.records_imported || 0 }}</span>
    </template>
    <template v-slot:[`item.target_list_names`]="slotProps">
      <slot v-if="$scopedSlots['cell-target_list_names']" name="cell-target_list_names" v-bind="slotProps" />
      <div v-else class="target-lists-display">
        <v-chip
          v-for="list_name in split_and_trim(slotProps.item.target_list_names, 3)"
          :key="list_name"
          small
          color="purple"
          text-color="white"
          class="mr-1 mb-1"
        >
          <v-icon small left>mdi-format-list-bulleted</v-icon>
          {{ list_name }}
        </v-chip>
        <span v-if="!slotProps.item.target_list_names || !slotProps.item.target_list_names.trim()" class="text-caption grey--text">
          No target lists
        </span>
      </div>
    </template>
    <template v-slot:[`item.target_bulk_message_names`]="slotProps">
      <slot v-if="$scopedSlots['cell-target_bulk_message_names']" name="cell-target_bulk_message_names" v-bind="slotProps" />
      <div v-else class="target-bulk-messages-display">
        <v-chip
          v-for="message_name in split_and_trim(slotProps.item.target_bulk_message_names, 3)"
          :key="message_name"
          small
          color="blue"
          text-color="white"
          class="mr-1 mb-1"
        >
          <v-icon small left>mdi-email-multiple</v-icon>
          {{ message_name }}
        </v-chip>
        <span v-if="!slotProps.item.target_bulk_message_names || !slotProps.item.target_bulk_message_names.trim()" class="text-caption grey--text">
          No target bulk message
        </span>
      </div>
    </template>
    <template v-slot:[`item.actions`]="slotProps">
      <slot v-if="$scopedSlots['cell-actions']" name="cell-actions" v-bind="slotProps" />
      <div v-else style="display: flex; gap: 8px;">
        <v-btn v-if="slotProps.item.state !== 'completed'" color="primary" small @click="on_click_view_preview(slotProps.item)">
          View Preview
        </v-btn>
      </div>
    </template>
  </v-data-table>
</template>

<script>
export default {
  name: 'ImportsDataTable',
  props: {
    headers: { type: Array, required: true },
    items: { type: Array, required: true },
    import_type: { type: String, required: true },
    upload_route_name: { type: String, required: true },
    items_per_page: { type: Number, default: 50 },
    loading: { type: Boolean, default: false },
    loading_text: { type: String, default: 'Loading imports...' },
    no_data_text: { type: String, default: 'No imports available' },
    footer_props: { type: Object, default: () => ({
      itemsPerPageText: 'Imports per page:',
      itemsPerPageOptions: [25, 50, 100],
      disableItemsPerPage: false,
      disablePagination: false
    }) }
  },
  methods: {
    format_status(status) {
      return this.$rc.pretty(status, 'enum_import_state');
    },
    get_status_color(status) {
      switch (status) {
        case 'uploaded': return 'blue';
        case 'preview-ready': return 'orange';
        case 'completed': return 'green';
        case 'failed': return 'red';
        case 'cancelled': return 'grey';
        default: return 'grey';
      }
    },
    split_and_trim(names_string, limit) {
      if (!names_string || !names_string.trim()) {
        return [];
      }
      const parts = names_string.split(',').map(s => s.trim()).filter(Boolean);
      return typeof limit === 'number' ? parts.slice(0, limit) : parts;
    },
    on_click_view_preview(import_item) {
      this.$router.push({
        name: this.upload_route_name,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        }
      });
    },
    on_click_download_file(import_item) {
      this.$rc.rest({
        spinner: true,
        endpoint: `GET: /api/v1/imports/${this.import_type}/download-file/:organization_id/:import_id`,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        },
        on_success: (data) => { window.open(data.download_url, '_blank'); },
        on_error: 'Unable to download import file'
      });
    },
    on_click_download_enriched_data(import_item) {
      this.$rc.rest({
        spinner: true,
        endpoint: `GET: /api/v1/imports/${this.import_type}/download-enriched-data/:organization_id/:import_id`,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        },
        on_success: (data) => { window.open(data.download_url, '_blank'); },
        on_error: 'Unable to download enriched data file'
      });
    }
  }
};
</script>

<style scoped>
.target-lists-display,
.target-bulk-messages-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 300px;
}
</style>

